import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { clearPendingCheckout } from '../../lib/pendingCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
// Extended from 8 → 20 polls (16s → 40s). Stripe webhooks usually beat the
// browser redirect, but on a slow link or a saturated webhook queue we'd
// rather wait an extra few seconds than show a false-failure page to a user
// whose card was actually charged.
const MAX_POLL_ATTEMPTS = 20;
const SUCCESS_REDIRECT_MS = 3000;
const TIMEOUT_MESSAGE =
  'Still processing on Stripe\u2019s side. Your slots will be applied automatically when Stripe confirms — refresh Settings in a moment, or contact support if you don\u2019t see them.';

/**
 * Return page after Stripe Checkout. Polls the backend until payment
 * resolves, then routes back to /settings. The backend's idempotent
 * status endpoint is what actually applies the slot increment, so it
 * doesn't matter how many times we poll — credit is only ever applied
 * once.
 */
export default function SlotTopUpReturn() {
  const navigate = useNavigate();
  const { authToken, user } = useAuth();
  const [state, setState] = useState({ phase: 'polling', data: null, error: null });
  const [attempts, setAttempts] = useState(0);

  // Tenant-scoped destination — every tenant has /{slug}/settings; super
  // admin (no workspaceId, unlikely path here) bounces to admin dashboard.
  const settingsPath = user?.workspaceId
    ? `/${user.workspaceId}/settings`
    : '/admin/dashboard';

  const pollStatus = useCallback(async (sessionId) => {
    // Two-tier probe (v3.10.7):
    //   1. If we have a JWT, hit the authenticated endpoint — full
    //      payload, tenant-scoped, applies the side-effect.
    //   2. If no JWT (incognito window closed mid-flow, session expired
    //      between Stripe and return) OR the authenticated call returns
    //      401, fall back to the public status endpoint so the UI can
    //      tell the user the truth instead of a misleading failure.
    //   The actual credit is ALWAYS applied — either by the poll OR by
    //   the Stripe webhook. We just need the return page to display the
    //   right state.
    try {
      if (authToken) {
        const res = await fetch(
          `${API_URL}/api/billing/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'COMPLETED') {
            setState({ phase: 'success', data, error: null, anonymous: false });
            return;
          }
          if (data.status === 'FAILED' || data.status === 'EXPIRED') {
            setState({ phase: 'failed', data, error: null, anonymous: false });
            return;
          }
          setAttempts((a) => a + 1);
          return;
        }
        if (res.status !== 401) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${res.status}`);
        }
        // 401 → fall through to anonymous probe.
      }

      const pubRes = await fetch(
        `${API_URL}/api/billing/checkout/status-public/${sessionId}`,
      );
      if (!pubRes.ok) {
        const err = await pubRes.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${pubRes.status}`);
      }
      const pubData = await pubRes.json();
      if (pubData.status === 'COMPLETED') {
        setState({ phase: 'success', data: pubData, error: null, anonymous: true });
      } else if (pubData.status === 'FAILED' || pubData.status === 'EXPIRED') {
        setState({ phase: 'failed', data: pubData, error: null, anonymous: true });
      } else {
        setAttempts((a) => a + 1);
      }
    } catch (e) {
      setState({ phase: 'failed', data: null, error: e.message, anonymous: !authToken });
    }
  }, [authToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState({ phase: 'failed', data: null, error: 'Missing session_id in URL' });
      return;
    }
    pollStatus(sessionId);
  }, [pollStatus]);

  useEffect(() => {
    if (state.phase !== 'polling') return;
    if (attempts >= MAX_POLL_ATTEMPTS) {
      setState({
        phase: 'failed',
        data: null,
        error: TIMEOUT_MESSAGE,
      });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    const t = setTimeout(() => pollStatus(sessionId), POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [attempts, state.phase, pollStatus]);

  // Auto-redirect on success (3s read pause, then back to workspace settings).
  // Anonymous returns (incognito with no JWT) require explicit Sign In —
  // there's nowhere meaningful to send them automatically.
  useEffect(() => {
    if (state.phase !== 'success' && state.phase !== 'failed') return;
    clearPendingCheckout();
    if (state.phase !== 'success') return;
    if (state.anonymous) return;
    const t = setTimeout(() => navigate(settingsPath), SUCCESS_REDIRECT_MS);
    return () => clearTimeout(t);
  }, [state.phase, state.anonymous, settingsPath, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="slot-topup-return">
      <Card className="max-w-md w-full border-slate-200 shadow-sm">
        <CardContent className="p-8 text-center">
          {state.phase === 'polling' && (
            <>
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Confirming Payment
              </h2>
              <p className="text-sm text-slate-600">
                We&apos;re checking with Stripe — this usually takes a few seconds.
              </p>
              <p className="text-xs text-slate-400 mt-3">Attempt {attempts + 1} of {MAX_POLL_ATTEMPTS}</p>
            </>
          )}

          {state.phase === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Payment Confirmed
              </h2>
              <p className="text-sm text-slate-600 mb-2">
                {state.data?.metadata?.quantity || 'Your'} pursuit slots have been added to your workspace.
              </p>
              {state.data?.amount && (
                <p className="text-xs text-slate-500 mb-2">
                  Charged ${state.data.amount.toFixed(2)} {(state.data.currency || 'usd').toUpperCase()}
                </p>
              )}
              {state.anonymous ? (
                <>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                    You&apos;re not signed in on this browser. Your purchase is
                    confirmed — sign back in to see the new slot count in
                    Settings.
                  </p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                    data-testid="login-after-payment-btn"
                  >
                    Sign In
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-400 mb-6">Redirecting to Settings…</p>
                  <Button
                    onClick={() => navigate(settingsPath)}
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                    data-testid="back-to-settings-btn"
                  >
                    Back to Settings
                  </Button>
                </>
              )}
            </>
          )}

          {state.phase === 'failed' && (
            <>
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {state.data?.status === 'EXPIRED' ? (
                  <Clock className="w-8 h-8 text-rose-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-rose-600" />
                )}
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Payment Not Completed
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                {state.error || 'The payment did not go through. No charges were made.'}
              </p>
              <Button
                onClick={() => navigate(settingsPath)}
                variant="outline"
                className="w-full border-slate-200"
                data-testid="back-to-settings-btn"
              >
                Back to Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
